/**
 * Wolof catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 3.
 *
 * `dir: 'ltr'`. Wolof at a mathematician's register. **Warrant → «Firnde»** (proof/evidence —
 * flagged). Scientific loanwords are used where Wolof standardly borrows (logic → Lojik, modal,
 * heuristic, scalar, geometry). W/M grades stay Latin (`::numbering-system/latn`). Wolof has NO
 * grammatical plural on the noun — the only CLDR cardinal category is `other`, so `frameActive`
 * declares just `other` (plus an explicit `=0`).
 *
 * NOTE (honesty over guessing): Wolof's standardized mathematical register is thin; this is a
 * best-effort draft flagged "expert-review suggested" in `Agent9_Batch3_REPORT.md`. Per
 * INV-STABLE-ID any correction changes only the string.
 */
import { P2 } from '../message-ids-phase2.js';
export const WO_CATALOG = {
    locale: 'wo',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Gëstu',
        [P2.sectionClaims]: 'Ay wax',
        [P2.sectionIntegrity]: 'Integrite',
        [P2.axisWarrantLabel]: 'Firnde',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Empirik',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Tekki} interp {Firi} testable {Mën a seetu} tested {Seetiku na} refuted {Weddiwu} program {Programu gëstu} philos {Filosofi} other {Xamuñ ko}}',
        [P2.axisModalLabel]: 'Modal',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Tolluwaay bu doole {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Mën a gëstu',
        [P2.stateRefuted]: 'Weddiwu',
        [P2.stateOutside]: 'Ci biti barab bi',
        [P2.verdictConflict]: 'Dafa juuyoo ak {constraints}',
        [P2.verdictOutside]: 'Dafa soxla {missing}',
        [P2.lossRegisterPlain]: 'Yombalees na ci làkk bu yomb — am na wér yu ñàkk.',
        [P2.lossRegisterKid]: 'Yombalees na ngir jàngkat yu ndaw — barina leeral yu ñàkk.',
        [P2.lossProfileDetail]: 'Profil bi mat sëkk Firnde / Empirik / Modal ñu boole ko fii.',
        [P2.lossConflictDetail]: 'Leeral yi ci juuyoo bi gàttal nañu leen ci gis-gis bii.',
        [P2.laneVerified]: 'Wóoral nañu ko',
        [P2.laneUnverified]: 'Wóoraluñu ko',
        [P2.laneConflict]: 'Juuyoo bi firndeelu na',
        [P2.constraintLogic]: 'Lojik',
        [P2.constraintLightBends]: 'Leer bi dafay coppe ci tool bu indi-gravite',
        [P2.constraintParallelPostulate]: 'Postilaa bu rëdd yu parallèl',
        [P2.claimScalarGravity]: 'Teori skaler bu gravite',
        [P2.claimNonEuclidean]: 'Geometri bu dul bu Euclide',
        [P2.claimSubstrateSr]: 'Firi substrate bu jokkalante bu rëy',
        [P2.edgeExtObservation]: 'Seetaanu bitim',
        [P2.edgeResourceSample]: 'Alal ju ñu bokk ngir waññ',
        [P2.edgeHeuristicSample]: 'Njuumte heuristik',
        [P2.edgeSharedSample]: 'Sukkandiku bu ñu bokk',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Ay tere',
        [P2.sectionEdges]: 'Ay catu',
        [P2.sectionFrame]: 'Kaadar',
        [P2.sectionExposure]: 'Feeñal',
        [P2.statusAdoptedFormal]: 'Nangu nañu ko (ci anam gu wóor)',
        [P2.statusAdoptedInterpretive]: 'Nangu nañu ko (ci firi)',
        [P2.statusSuspended]: 'Taxawal nañu ko',
        [P2.edgeEffectConstraint]: 'Dafay sos tere (mën na weddi)',
        [P2.edgeEffectResource]: 'Alal — amul njeexital ci àtte yi',
        [P2.edgeEffectHeuristic]: 'Heuristik — amul njeexital ci àtte yi',
        [P2.edgeEffectShared]: 'Sukkandiku bu ñu bokk — amul njeexital ci àtte yi',
        [P2.frameActive]: '{count, plural, =0 {Amul tere yu jëf} other {# tere yu jëf}}',
        [P2.exposureNone]: 'Amul sukkandiku ci firi',
        [P2.exposureDep]: 'Dafa sukkandiku ci {id}',
        [P2.laneTrackMachine]: 'Masin',
        [P2.laneTrackExpert]: 'Boroom xam-xam',
        [P2.laneTrackCommunity]: 'Mbooloo',
        [P2.expertAudited]: 'Seetlu nañu ko',
        [P2.expertInProgress]: 'Mu ngi dox',
        [P2.expertNotReviewed]: 'Seetaatuñu ko',
        [P2.communityFlags]: 'Am na ay màndarga',
        [P2.communityNone]: 'Amul màndarga',
        [P2.controlChallenge]: 'Weddi',
        [P2.controlActivate]: 'Yëngal',
        [P2.controlMakeInterpretive]: 'Def ko firi',
        [P2.controlImportEdge]: 'Dugal cat',
        [P2.controlPromote]: 'Yokk',
        [P2.controlUndo]: 'Dëppal',
        [P2.controlRedo]: 'Defaat',
        [P2.controlRegister]: 'Bind',
        [P2.promoteRejected]: 'Yokk bi ñu ko weddi',
        [P2.promoteAccepted]: 'Yokk bi ñu ko nangu',
        [P2.promoteReasonRefutationTerminal]: 'Firnde mënul faj weddi.',
        [P2.promoteReasonKeystone]: 'Yokk bu am solo dafa soxla ndimbal empirik.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Xeetu empirik bi taxawul.',
        [P2.promoteReasonModalNotFixed]: 'Tolluwaayu modal bi taxawul.',
        [P2.promoteReasonWarrantNotImproved]: 'Tolluwaayu firnde bi gënul.',
        [P2.evidenceComputation]: "{engine} computed {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Result rounded to {dp} decimal places",
    },
};
//# sourceMappingURL=wo.js.map